/**
 * Making the phone buzz, by whichever road exists.
 *
 * The web road does not exist inside the installed app. Silvia's phone,
 * bundle 18, with android.permission.VIBRATE declared and granted, reported
 * "no API": navigator.vibrate is simply absent from an Android WebView. No
 * permission and no setting brings it back — the WebView does not implement
 * the Vibration API at all, and three attempts to fix this from the web side
 * were three attempts at a door that is not there.
 *
 * So there are two roads and this picks whichever is open:
 *
 *   native   @capacitor/haptics, through the bridge Capacitor injects into
 *            the WebView. Present only in the installed app, and only from
 *            the bundle that ships the plugin.
 *   web      navigator.vibrate, in Chrome and any ordinary browser, where it
 *            has always worked.
 *
 * A PATTERN is the other trap. [500, 200, 500] asks for buzz-pause-buzz, and a
 * good many Android phones quietly drop patterns from the web and honour only a
 * single duration — the call returns true, and nothing moves. Silvia's phone
 * answered 'web' and stayed still. So the pattern is played by hand: one buzz,
 * a real pause, another buzz. Every road here ends in a single-duration call,
 * which is the one every device understands.
 *
 * Returns what actually happened, because a caller that cannot tell the
 * difference between "it buzzed" and "nothing happened" ends up telling
 * somebody their phone is broken.
 */

/**
 * The native buzzer, reached the way Capacitor means it to be reached.
 *
 * This is where it was failing, and the reason is small and total. The web app
 * carries @capacitor/local-notifications as a dependency, so LocalNotifications
 * registers itself and appears on the bridge — but it does NOT carry the
 * haptics package. window.Capacitor.Plugins.Haptics was therefore undefined
 * however many times MeMo looked; the code fell through to navigator.vibrate,
 * and the WebView answered yes and did nothing. Three rounds of "it still does
 * not buzz" were all the same missing registration.
 *
 * registerPlugin() is the documented way to reach a plugin that exists on the
 * NATIVE side without shipping its JavaScript wrapper — @capacitor/haptics is
 * already in the Android shell. So: no new dependency for the web bundle, and
 * the proxy simply fails (caught below) anywhere there is no bridge.
 */
let hapticsPlugin;
async function haptics() {
  if (hapticsPlugin !== undefined) return hapticsPlugin;
  hapticsPlugin = null;
  try {
    if (typeof window === 'undefined' || !window.Capacitor) return hapticsPlugin;
    const { registerPlugin } = await import('@capacitor/core');
    hapticsPlugin = registerPlugin('Haptics');
  } catch (_) {
    hapticsPlugin = null;
  }
  return hapticsPlugin;
}

/** 'native' | 'web' | 'no-api' | 'refused' */
export async function buzz(pattern = [500, 200, 500]) {
  const list = Array.isArray(pattern) ? pattern : [pattern];

  // ── native ────────────────────────────────────────────────────────────────
  try {
    const H = await haptics();
    if (H?.vibrate) {
      // The plugin buzzes for a duration; it has no notion of a pattern. The
      // odd entries are the buzzes and the even ones the silences between,
      // which is what the web API means by a pattern, so it is played out by
      // hand rather than approximated with one long one.
      for (let i = 0; i < list.length; i++) {
        if (i % 2 === 0) await H.vibrate({ duration: list[i] });
        else await new Promise(r => setTimeout(r, list[i]));
      }
      return 'native';
    }
  } catch (_) {
    // A native failure is not the end: the web road may still be open.
  }

  // ── web ───────────────────────────────────────────────────────────────────
  try {
    if (!navigator.vibrate) return 'no-api';
    // One buzz at a time, with the pauses done in JavaScript. Handing the
    // whole array to the phone is what silently does nothing.
    let ok = false;
    for (let i = 0; i < list.length; i++) {
      if (i % 2 === 0) { ok = navigator.vibrate(list[i]) !== false || ok; }
      else await new Promise(r => setTimeout(r, list[i]));
    }
    return ok ? 'web' : 'refused';
  } catch (_) {
    return 'refused';
  }
}

export default buzz;
